/**
 * Field constants and formatting shared by the Hospitals list's create modal
 * and the Hospital Details form (EN-5152 / EN-5172).
 *
 * ⚠️ In this schema `cms_hospitalgroup` is a HOSPITAL and `cms_hospital` is a
 * WARD, so "hospital" here always means the group.
 */

/**
 * The timezone list Retool offered on both its create modal and its detail
 * form; `Europe/Dublin` was the default. A closed list rather than an endpoint:
 * there is no timezone table, and the column is a free-text IANA name that the
 * notification scheduler resolves — offering every zone would imply a
 * configurability that does not exist.
 */
export const TIMEZONES = [
  "Africa/Johannesburg",
  "America/Los_Angeles",
  "America/New_York",
  "Canada/Eastern",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Brussels",
  "Europe/Dublin",
  "Europe/London",
  "US/Eastern",
  "UTC",
];

export const DEFAULT_TIMEZONE = "Europe/Dublin";

/** Name and code are capped at 70 characters by the hospital request DTOs. */
export const MAX_TEXT_LENGTH = 70;

/**
 * `HH:mm` for a `<input type="time">`, from the API's `HH:mm:ss`.
 *
 * The value is a LOCAL time in the hospital's own timezone, not an instant —
 * which is why it is sliced rather than parsed through a date. Retool sent it
 * as `notificationTime?.replace('Z', '')`, stripping a UTC marker its own
 * time picker had added to a value that was never UTC.
 */
export function toTimeInputValue(
  notificationTime: string | null | undefined
): string {
  if (!notificationTime) return "";
  return notificationTime.slice(0, 5);
}

/**
 * `HH:mm:ss` for the API, from a time input's `HH:mm`.
 *
 * Seconds are appended rather than left off: the column is a `TimeSpan` and the
 * API parses the string, so a two-part value would be read as `HH:mm` = hours
 * and minutes in some cultures and minutes and seconds in others.
 */
export function toNotificationTime(timeInputValue: string): string {
  if (!timeInputValue) return "00:00:00";
  const [hours = "00", minutes = "00"] = timeInputValue.split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:00`;
}
